import { Component, forwardRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { map } from 'rxjs/operators';

import { TIMES15 } from 'core/date/hours-mins';
import { TimeFormatPipe } from 'shared/pipes/time-format.pipe';
import { MatAutocomplete } from '@angular/material/autocomplete';

const timePipe = new TimeFormatPipe();

export interface TimeDropdownOption {
  label: string;
  value: string;
}
@Component({
  selector: 'app-time-input',
  templateUrl: './time-input.component.html',
  styleUrls: ['./time-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimeInputComponent),
      multi: true,
    },
  ],
})
export class TimeInputComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() label = 'Time';
  @Input() onlyAfter: string; // template literal of the form '13:00'
  @Input() formControl: FormControl;
  @ViewChild(MatAutocomplete, { static: false }) autocomplete: MatAutocomplete;
  value: string;
  options: TimeDropdownOption[] = [];
  filteredOptions: TimeDropdownOption[] = [];

  /**
   *
   */
  constructor() {
  }

  ngOnInit() {
    // Get 24-hour time options, i.e. 00:00, 00:15, 00:30, 00:45 etc.
    this.options = TIMES15.map(time24 => ({
      label: timePipe.transform(time24),
      value: time24
    }));

    this.setFilteredOptionsToOnlyAfter();
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  ngOnChanges(changes: SimpleChanges) {
    if (changes.onlyAfter) {
      this.setFilteredOptionsToOnlyAfter();
    }
  }

  /**
   * Triggers when autocomplete panel is opened.
   * This scrolls the panel to the selected value.
   */
  autoCompleteOpened() {
    // wait 50ms for options to render
    setTimeout(() => {
      const currentValue = this.formControl.value;
      const children = this.autocomplete.panel?.nativeElement?.children || [];
      const nearestIndex = this.getNearestIndex(this.filteredOptions, currentValue);
      try {
        children[nearestIndex]?.scrollIntoView();
        this.autocomplete._keyManager.setActiveItem(nearestIndex);
      } catch (e) {
        // In case the index is out of bounds
        console.error(e);
      }
    }, 50);
  }

  setFilteredOptionsToOnlyAfter() {
    if (!this.onlyAfter) {
      this.filteredOptions = this.options;
      return;
    }

    const tempOnlyAfter = this.onlyAfter;
    const afterIndex = this.getNearestIndex(this.options, tempOnlyAfter);

    // TODO: This would actually filter out the selected date and not allow midnight,
    // but this seems fine.
    this.filteredOptions = this.options.slice(afterIndex + 1);
  }

  private getNearestIndex(options: TimeDropdownOption[], value: string) {
    // Our dropdown contains only 15 minute intervals, but input can be any minute.
    // First check to see if the input is in our list (easy case)
    let nearestIndex = options.findIndex(option => option.value === value);
    if (nearestIndex === -1) {
      nearestIndex = options.findIndex(option => Number(option.value) > Number(value));
    }

    return nearestIndex;
  }

  writeValue(value: string): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void { }
}
